import { Link, useNavigate, useParams } from "react-router";
import { SchrijvenTopicRenderer } from "../../components/schrijven/SchrijvenTopicRenderer";
import { GrammaticaToc } from "../../components/study/GrammaticaToc";
import { TopicRenderer } from "../../components/study/TopicRenderer";
import {
  getGrammaticaTopic,
  tocHeadingsFor,
} from "../../data/study/grammatica-index";
import { Button } from "../../components/ui/Button";
import { useT } from "../../lib/use-t";

export function GrammaticaReaderPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const t = useT();
  const found = slug ? getGrammaticaTopic(slug) : undefined;

  if (!found) {
    return (
      <div className="mx-auto w-full max-w-[1080px] px-5 sm:px-6 py-6 sm:py-8 space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("grammatica.reader.notFoundTitle")}
        </h1>
        <p className="font-serif-italic text-sm text-[var(--ink-muted)]">
          {slug
            ? t("grammatica.reader.notFoundDescriptionFmt", { slug })
            : t("grammatica.reader.notFoundNoSlug")}
        </p>
        <Link
          to="/grammatica"
          className="inline-flex items-center gap-2 text-sm text-[var(--ink-muted)] transition-colors hover:text-[var(--ink)]"
        >
          <span aria-hidden>←</span>
          <span>{t("grammatica.reader.allChapters")}</span>
        </Link>
      </div>
    );
  }

  const { topic, exam } = found;
  const headings = tocHeadingsFor(topic.sections);
  // Why: per-topic drilling doesn't exist; route to the module's practice hub.
  const practiceTo = exam === "schrijven" ? "/schrijven" : "/spreken";

  return (
    <div className="mx-auto w-full max-w-[1080px] px-5 sm:px-6 py-6 sm:py-8 pb-24 sm:pb-28">
      <Link
        to="/grammatica"
        className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-[var(--border-strong)] bg-[var(--panel)] px-3.5 py-2 text-[13px] font-semibold text-[var(--ink-muted)] transition-colors duration-150 ease-out hover:bg-[var(--panel-soft)] hover:text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
      >
        <span aria-hidden>←</span>
        <span>{t("grammatica.reader.back")}</span>
      </Link>

      <div className="grid items-start gap-8 lg:grid-cols-[1fr_220px]">
        <div className="min-w-0">
          {exam === "schrijven" ? (
            <SchrijvenTopicRenderer topic={topic} />
          ) : (
            <TopicRenderer topic={topic} />
          )}

          <footer className="mt-8 flex max-w-prose flex-wrap items-center gap-3 border-t border-[var(--border)] pt-6">
            <Button variant="warm" size="md" onClick={() => navigate(practiceTo)}>
              {t("grammatica.reader.practiceCta")}
            </Button>
          </footer>
        </div>

        {headings.length > 0 ? (
          <aside className="hidden lg:block">
            <GrammaticaToc
              label={t("grammatica.reader.tocTitle")}
              topicTitle={topic.title}
              headings={headings}
            />
          </aside>
        ) : null}
      </div>
    </div>
  );
}
